import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchDailyCash } from "../../store/slices/financeSlice";
import { fetchMe } from "../../store/slices/authSlice";
import { financeService } from "../../services/financeService";
import { authService } from "../../services/authService";
import Icon from "../../components/Common/Icon";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import PaymentCollectModal from "../../components/finance/PaymentCollectModal";
import AdvancedTable from "../../components/Common/AdvancedTable";
import { useToast } from "../../context/ToastContext";

const fmtTL = (n) =>
  (n == null ? 0 : Number(n)).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 2,
  });

const today = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD

const METHOD_LABEL = { CASH: "Nakit", CARD: "Kart", TRANSFER: "Havale" };
const RECEIVABLE_STATUS = {
  // Randevu takvimiyle aynı renk dili: Planlandı mavi, Tamamlandı yeşil.
  SCHEDULED: { label: "Planlandı", cls: "bg-blue-500/10 text-blue-500" },
  COMPLETED: { label: "Tamamlandı", cls: "bg-success/10 text-success" },
};
const SOURCE_LABEL = {
  APPOINTMENT: "Randevu",
  PRODUCT_SALE: "Ürün Satışı",
  MANUAL: "Elle",
  PAYROLL: "Maaş/Prim",
  OTHER: "Diğer",
};

const TABS = [
  { key: "cash", label: "Günlük Kasa", icon: "point_of_sale" },
  { key: "receivables", label: "Alacaklar", icon: "request_quote" },
];

export default function CashRegisterPage() {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { daily, isLoading, error } = useSelector((state) => state.finance);
  const role = useSelector((state) => state.auth.user?.role);

  // Staff yalnızca Alacaklar sekmesini görür; günlük kasa yönetici işidir.
  const isStaff = role === "STAFF";
  const visibleTabs = isStaff ? TABS.filter((t) => t.key === "receivables") : TABS;

  // Kasa ekranı kilidi: MANAGER oturumda bir kez şifre girer.
  const [unlocked, setUnlocked] = useState(
    () => isStaff || sessionStorage.getItem("cashUnlocked") === "true"
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(
    isStaff || searchParams.get("tab") === "receivables" ? "receivables" : "cash"
  );
  const [date, setDate] = useState(today());
  const [initialAddOpen, setInitialAddOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      setInitialAddOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Randevu detayından gelen deep-link: alacaklar sekmesi + müşteri araması
  const initialReceivableQuery = searchParams.get("q") ?? "";

  const selectTab = (key) => {
    setTab(key);
    // URL'i temizle ki yenilemede filtre takılı kalmasın
    if (searchParams.has("tab") || searchParams.has("q")) setSearchParams({}, { replace: true });
  };

  useEffect(() => {
    if (isStaff || !unlocked) return; // staff'ın günlük kasa ucuna erişimi yok; kilitliyken çekme
    dispatch(fetchDailyCash(date));
  }, [dispatch, date, isStaff, unlocked]);

  if (!unlocked) {
    return <CashLockGate onUnlock={() => setUnlocked(true)} showToast={showToast} />;
  }

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="point_of_sale" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Kasa</h1>
            <p className="text-on-surface-variant text-sm">
              Günlük tahsilat, gider ve açık alacak takibi.
            </p>
          </div>
        </div>

        {tab === "cash" && (
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary"
          />
        )}
      </div>

      {/* Sekmeler */}
      <div className="flex items-center gap-1 border-b border-outline-variant">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => selectTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${
              tab === t.key
                ? "text-primary border-primary"
                : "text-on-surface-variant border-transparent hover:text-primary"
            }`}
          >
            <Icon name={t.icon} className="!text-[18px]" /> {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      {tab === "cash" && (
        <CashTab
          daily={daily}
          isLoading={isLoading}
          date={date}
          onChanged={() => dispatch(fetchDailyCash(date))}
          showToast={showToast}
          initialAddOpen={initialAddOpen}
        />
      )}
      {tab === "receivables" && <ReceivablesTab showToast={showToast} initialQuery={initialReceivableQuery} />}
    </div>
  );
}

/* ---------------------------------------------------------- Kilit ekranı */

function CashLockGate({ onUnlock, showToast }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // "Kasa şifremi unuttum" akışı: eski kasa şifresi sorulmaz, kimlik hesabın
  // giriş şifresiyle kanıtlanır (backend zaten böyle doğruluyor).
  const [resetting, setResetting] = useState(false);

  // Eski oturumlarda hasCashPassword bayrağını tazele (doğru dalı göster).
  useEffect(() => {
    dispatch(fetchMe());
  }, [dispatch]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await authService.verifyCashPassword(pwd);
      if (res.data?.data?.valid) {
        sessionStorage.setItem("cashUnlocked", "true");
        showToast("Kasa kilidi açıldı.", "success");
        onUnlock();
      } else {
        setError("Kasa şifresi hatalı.");
      }
    } catch (err) {
      setError(err.response?.data?.error ?? "Doğrulama başarısız.");
    } finally {
      setBusy(false);
    }
  };

  const needsSetup = !user?.hasCashPassword;

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-full max-w-sm bg-surface-card rounded-3xl shadow-modal border border-outline-variant p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
          <Icon name="lock" className="!text-[32px]" />
        </div>
        <h1 className="text-xl font-bold text-on-surface">
          {resetting ? "Yeni Kasa Şifresi" : "Kasa Kilitli"}
        </h1>
        <p className="text-on-surface-variant text-sm mt-1 mb-6">
          {resetting
            ? "Kimliğinizi hesap giriş şifrenizle doğrulayın ve yeni bir kasa şifresi belirleyin. Eski kasa şifreniz gerekmez."
            : needsSetup
            ? "Kasa ekranını görüntülemek için önce bir kasa şifresi belirlemelisiniz."
            : "Devam etmek için kasa ekranı şifrenizi girin."}
        </p>

        {resetting ? (
          <CashPasswordResetForm
            onCancel={() => setResetting(false)}
            onDone={() => {
              // Yeni şifre, hesap giriş şifresi doğrulanarak belirlendi; bu kasa
              // şifresinden daha güçlü bir kimlik kanıtı olduğu için kilit açılır.
              sessionStorage.setItem("cashUnlocked", "true");
              showToast("Yeni kasa şifresi oluşturuldu.", "success");
              dispatch(fetchMe());
              onUnlock();
            }}
          />
        ) : needsSetup ? (
          <button
            onClick={() => navigate("/cash-password")}
            className="w-full bg-primary text-white rounded-xl py-3 font-semibold hover:opacity-90 transition"
          >
            Kasa Şifresi Belirle
          </button>
        ) : (
          <form onSubmit={submit} className="space-y-3 text-left">
            {error && (
              <div className="flex items-center gap-2 text-sm text-error bg-error/10 rounded-xl px-4 py-2.5">
                <Icon name="error" className="!text-[18px]" /> {error}
              </div>
            )}
            <input
              type="password"
              autoFocus
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Kasa şifresi"
              className="w-full border border-outline-variant rounded-xl px-4 py-3 bg-white outline-none focus:border-primary text-center tracking-widest"
            />
            <button
              type="submit"
              disabled={busy || !pwd}
              className="w-full bg-primary text-white rounded-xl py-3 font-semibold hover:opacity-90 transition disabled:opacity-50"
            >
              {busy ? "Doğrulanıyor…" : "Kilidi Aç"}
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setResetting(true);
              }}
              className="w-full text-xs text-primary hover:underline transition py-1 font-semibold"
            >
              Kasa şifremi unuttum — yeni şifre oluştur
            </button>
            <button
              type="button"
              onClick={() => navigate("/cash-password")}
              className="w-full text-xs text-on-surface-variant hover:text-primary transition py-1"
            >
              Kasa şifresini değiştir
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Kasa şifresi unutulduğunda kurtarma: eski kasa şifresi SORULMAZ, kimlik hesabın
 * giriş şifresiyle kanıtlanır. Backend (/accounts/cash-password) zaten tam olarak
 * bunu doğruluyor; burada kilit ekranından erişilebilir hale getiriliyor.
 */
function CashPasswordResetForm({ onCancel, onDone }) {
  const [loginPassword, setLoginPassword] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPwd.length < 4) {
      setError("Kasa şifresi en az 4 karakter olmalı.");
      return;
    }
    if (newPwd !== confirm) {
      setError("Yeni kasa şifreleri eşleşmiyor.");
      return;
    }
    setBusy(true);
    try {
      await authService.setCashPassword({ loginPassword, newCashPassword: newPwd });
      onDone();
    } catch (err) {
      setError(err.response?.data?.error ?? "İşlem başarısız.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full border border-outline-variant rounded-xl px-4 py-3 bg-white outline-none focus:border-primary";

  return (
    <form onSubmit={submit} className="space-y-3 text-left">
      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 rounded-xl px-4 py-2.5">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}
      <input
        type="password"
        autoFocus
        autoComplete="current-password"
        value={loginPassword}
        onChange={(e) => setLoginPassword(e.target.value)}
        placeholder="Hesap (giriş) şifreniz"
        className={inputCls}
      />
      <input
        type="password"
        autoComplete="new-password"
        value={newPwd}
        onChange={(e) => setNewPwd(e.target.value)}
        placeholder="Yeni kasa şifresi"
        className={inputCls}
      />
      <input
        type="password"
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder="Yeni kasa şifresi (tekrar)"
        className={inputCls}
      />
      <p className="text-[11px] text-on-surface-variant">En az 4 karakter.</p>
      <button
        type="submit"
        disabled={busy || !loginPassword || !newPwd || !confirm}
        className="w-full bg-primary text-white rounded-xl py-3 font-semibold hover:opacity-90 transition disabled:opacity-50"
      >
        {busy ? "Kaydediliyor…" : "Yeni Kasa Şifresini Oluştur"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="w-full text-xs text-on-surface-variant hover:text-primary transition py-1"
      >
        Vazgeç
      </button>
    </form>
  );
}

/* --------------------------------------------------------------- Kartlar */

const ACCENTS = {
  primary: "bg-primary/10 text-primary",
  success: "bg-success/10 text-success",
  error: "bg-error/10 text-error",
  gold: "bg-tier-gold/10 text-tier-gold",
};

function StatCard({ label, value, icon, accent = "primary", hint }) {
  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${ACCENTS[accent] ?? ACCENTS.primary}`}>
        <Icon name={icon} className="!text-[20px]" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-on-surface-variant truncate">{label}</p>
        <p className="text-lg font-bold text-on-surface">{fmtTL(value)}</p>
        {hint && <p className="text-[11px] text-on-surface-variant truncate">{hint}</p>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ Günlük Kasa */

function CashTab({ daily, isLoading, date, onChanged, showToast, initialAddOpen }) {
  const [addOpen, setAddOpen] = useState(initialAddOpen || false);
  const [openDayOpen, setOpenDayOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  useEffect(() => {
    if (initialAddOpen) {
      setAddOpen(true);
    }
  }, [initialAddOpen]);

  const methodTotals = daily?.byMethod ?? [];
  const txs = daily?.transactions ?? [];
  const pending = daily?.pending ?? [];

  const dayStatus = daily?.dayStatus ?? "NONE";
  const locked = dayStatus === "CLOSED";

  const closeDay = () =>
    setConfirmModal({
      isOpen: true,
      title: "Günü Kesinleştir",
      message:
        "Bu gün kapatılıp kesinleştirilecek. Kesinleşen güne yeni gelir/gider eklenemez ve kayıtlar değiştirilemez. Devam edilsin mi?",
      type: "warning",
      onConfirm: () =>
        financeService
          .closeDay(date)
          .then(() => {
            showToast("Gün kesinleştirildi.", "success");
            onChanged();
          })
          .catch((err) => showToast(err.response?.data?.error ?? "İşlem başarısız.", "error")),
    });

  const columns = [
    {
      key: "type",
      header: "Tür",
      type: "string",
      sortable: true,
      render: (v) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
          v === "INCOME"
            ? "bg-success/15 text-success border border-success/20"
            : "bg-error/15 text-error border border-error/20"
        }`}>
          {v === "INCOME" ? "Gelir" : "Gider"}
        </span>
      ),
    },
    {
      key: "category",
      header: "Kategori",
      type: "string",
      sortable: true,
      render: (v) => v || "—",
    },
    {
      key: "source",
      header: "Kaynak",
      type: "string",
      sortable: true,
      render: (v) => SOURCE_LABEL[v] ?? v,
    },
    {
      key: "customerName",
      header: "Müşteri",
      type: "string",
      sortable: true,
      render: (v) => v || "—",
    },
    {
      key: "paymentMethod",
      header: "Yöntem",
      type: "string",
      sortable: true,
      render: (v) => (v ? METHOD_LABEL[v] : "—"),
    },
    {
      key: "amount",
      header: "Tutar",
      type: "number",
      sortable: true,
      render: (v, row) => (
        <span className={`font-bold ${row.type === "INCOME" ? "text-success" : "text-error"}`}>
          {row.type === "INCOME" ? "+" : "−"}{fmtTL(v)}
        </span>
      ),
    },
  ];

  const renderActions = locked
    ? undefined
    : (t) => (
        <button onClick={() => remove(t)} title="Sil" className="p-1.5 rounded-lg text-error hover:bg-error/10 transition">
          <Icon name="delete" className="!text-[18px]" />
        </button>
      );

  const remove = (t) =>
    setConfirmModal({
      isOpen: true,
      title: "İşlemi Sil",
      message: `${fmtTL(t.amount)} tutarındaki ${t.type === "INCOME" ? "gelir" : "gider"} kaydı silinecek. Emin misiniz?`,
      type: "danger",
      onConfirm: () =>
        financeService
          .deleteTransaction(t.id)
          .then(() => {
            showToast("İşlem silindi.", "success");
            onChanged();
          })
          .catch((err) => showToast(err.response?.data?.error ?? "İşlem başarısız.", "error")),
    });

  return (
    <div className="space-y-4">
      {/* Gün oturumu (aç / kapat / kesinleştir) */}
      <DaySessionBanner
        daily={daily}
        dayStatus={dayStatus}
        onOpen={() => setOpenDayOpen(true)}
        onClose={closeDay}
      />

      {/* Özet kartları — yalnız ONAYLANMIŞ hareketlerden (mutlak kasa) */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatCard label="Günlük Gelir" value={daily?.totalIncome} icon="trending_up" accent="success" />
        <StatCard label="Günlük Gider" value={daily?.totalExpense} icon="trending_down" accent="error" />
        <StatCard
          label="Mutlak Kasa (Net)"
          value={daily?.net}
          icon="account_balance_wallet"
          accent="primary"
          hint="Gün sonu: onaylı giren − çıkan"
        />
        <StatCard
          label={locked ? "Kapanış Kasası" : "Devir + Net"}
          value={daily?.closingBalance}
          icon="savings"
          accent="gold"
          hint={`Açılış devri: ${fmtTL(daily?.openingBalance)}`}
        />
      </div>

      {/* Onay bekleyenler (stok satış/alımından otomatik gelen kayıtlar) */}
      {!locked && <PendingApprovals pending={pending} onChanged={onChanged} showToast={showToast} />}

      {/* Ödeme yöntemi kırılımı */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {methodTotals.map((m) => (
          <div key={m.method} className="bg-surface-card rounded-2xl border border-outline-variant p-4">
            <p className="text-xs font-semibold text-on-surface-variant mb-2 flex items-center gap-1.5">
              <Icon name={m.method === "CASH" ? "payments" : m.method === "CARD" ? "credit_card" : "account_balance"} className="!text-[16px]" />
              {METHOD_LABEL[m.method]}
            </p>
            <div className="flex items-center justify-between text-sm">
              <span className="text-success">+{fmtTL(m.income)}</span>
              <span className="text-error">−{fmtTL(m.expense)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-on-surface-variant">{txs.length} hareket</p>
        {locked ? (
          <span className="text-xs font-semibold text-on-surface-variant flex items-center gap-1.5">
            <Icon name="lock" className="!text-[16px]" /> Gün kesinleşti — salt okunur
          </span>
        ) : (
          <button
            onClick={() => setAddOpen(true)}
            className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-xs hover:opacity-90 transition flex items-center gap-1.5"
          >
            <Icon name="add" className="!text-[16px]" /> Gelir / Gider Ekle
          </button>
        )}
      </div>

      {/* Hareket listesi */}
      {isLoading ? (
        <div className="bg-surface-card rounded-2xl p-8 border border-outline-variant text-center text-on-surface-variant">
          Yükleniyor…
        </div>
      ) : (
        <AdvancedTable
          columns={columns}
          data={txs}
          actions={renderActions}
          searchPlaceholder="Kasada ara..."
        />
      )}

      {addOpen && (
        <AddTransactionModal
          date={date}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            onChanged();
          }}
          showToast={showToast}
        />
      )}

      {openDayOpen && (
        <OpenDayModal
          date={date}
          onClose={() => setOpenDayOpen(false)}
          onSaved={() => {
            setOpenDayOpen(false);
            onChanged();
          }}
          showToast={showToast}
        />
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}

/* ------------------------------------------------- Onay bekleyen kayıtlar */

function PendingApprovals({ pending, onChanged, showToast }) {
  if (!pending || pending.length === 0) return null;
  return (
    <div className="bg-tier-gold/5 border border-tier-gold/30 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-2 border-b border-tier-gold/20">
        <Icon name="pending_actions" className="!text-[20px] text-tier-gold" />
        <span className="font-bold text-on-surface text-sm">
          Onay Bekleyen Kasa Kayıtları
        </span>
        <span className="bg-tier-gold text-white rounded-full text-[10px] px-1.5 py-0.5 leading-none">
          {pending.length}
        </span>
        <span className="text-xs text-on-surface-variant ml-1">
          Stok satış/alımından geldi — onaylayınca kasaya yansır.
        </span>
      </div>
      <div className="divide-y divide-tier-gold/15">
        {pending.map((t) => (
          <PendingRow key={t.id} t={t} onChanged={onChanged} showToast={showToast} />
        ))}
      </div>
    </div>
  );
}

function PendingRow({ t, onChanged, showToast }) {
  const [method, setMethod] = useState(t.paymentMethod ?? "CASH");
  const [busy, setBusy] = useState(false);
  const isIncome = t.type === "INCOME";

  const approve = async () => {
    setBusy(true);
    try {
      await financeService.approveTransaction(t.id, method);
      showToast("Kayıt onaylandı, kasaya yansıdı.", "success");
      onChanged();
    } catch (err) {
      showToast(err.response?.data?.error ?? "Onay başarısız.", "error");
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await financeService.deleteTransaction(t.id);
      showToast("Kayıt reddedildi.", "success");
      onChanged();
    } catch (err) {
      showToast(err.response?.data?.error ?? "İşlem başarısız.", "error");
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm">
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
        isIncome ? "bg-success/15 text-success" : "bg-error/15 text-error"
      }`}>
        {isIncome ? "Gelir" : "Gider"}
      </span>
      <span className="font-medium text-on-surface flex-1 min-w-[140px]">
        {t.category || "—"}
        {t.customerName && <span className="text-on-surface-variant font-normal"> · {t.customerName}</span>}
      </span>
      <span className={`font-bold ${isIncome ? "text-success" : "text-error"}`}>
        {isIncome ? "+" : "−"}{fmtTL(t.amount)}
      </span>
      <select
        value={method}
        onChange={(e) => setMethod(e.target.value)}
        disabled={busy}
        className="border border-outline-variant rounded-lg px-2 py-1 text-xs bg-white outline-none focus:border-primary"
      >
        <option value="CASH">Nakit</option>
        <option value="CARD">Kart</option>
        <option value="TRANSFER">Havale</option>
      </select>
      <button
        onClick={approve}
        disabled={busy}
        className="bg-success text-white rounded-lg px-3 py-1.5 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1"
      >
        <Icon name="check" className="!text-[16px]" /> Onayla
      </button>
      <button
        onClick={reject}
        disabled={busy}
        title="Reddet (sil)"
        className="p-1.5 rounded-lg text-error hover:bg-error/10 transition disabled:opacity-50"
      >
        <Icon name="close" className="!text-[18px]" />
      </button>
    </div>
  );
}

/* --------------------------------------------------------- Gün oturumu */

const DAY_BADGE = {
  NONE: { label: "Açılmadı", cls: "bg-on-surface-variant/10 text-on-surface-variant", icon: "lock_open" },
  OPEN: { label: "Açık", cls: "bg-success/15 text-success", icon: "lock_open" },
  CLOSED: { label: "Kesinleşti", cls: "bg-on-surface-variant/15 text-on-surface-variant", icon: "lock" },
};

function DaySessionBanner({ daily, dayStatus, onOpen, onClose }) {
  const badge = DAY_BADGE[dayStatus] ?? DAY_BADGE.NONE;
  const fmtTime = (t) => (t ? new Date(t).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${badge.cls}`}>
          <Icon name={badge.icon} className="!text-[20px]" />
        </div>
        <div>
          <p className="text-sm font-bold text-on-surface flex items-center gap-2">
            Gün Durumu
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${badge.cls}`}>{badge.label}</span>
          </p>
          <p className="text-xs text-on-surface-variant">
            {dayStatus === "NONE" && "Bu gün henüz açılmadı. Gün içi işlem yapmak için günü açın."}
            {dayStatus === "OPEN" &&
              `Açılış: ${fmtTime(daily?.openedAt)} · Açılış devri: ${fmtTL(daily?.openingBalance)}`}
            {dayStatus === "CLOSED" && `Kapanış: ${fmtTime(daily?.closedAt)} · Kapanış kasası: ${fmtTL(daily?.closingBalance)}`}
          </p>
          {/* Otomatik açılan günde açılış bakiyesi sayılmadı, devredildi.
              Yönetici sayım tutarını girerek günü devralabilir. */}
          {dayStatus === "OPEN" && daily?.autoOpened && (
            <p className="text-[11px] text-tier-gold mt-0.5 flex items-center gap-1">
              <Icon name="info" className="!text-[14px]" />
              Gün ilk işlemle otomatik açıldı; açılış bakiyesi sayılmadı, önceki kapanıştan devredildi.
            </p>
          )}
        </div>
      </div>
      {dayStatus === "OPEN" && daily?.autoOpened && (
        <button
          onClick={onOpen}
          className="border border-tier-gold/40 text-tier-gold rounded-xl px-4 py-2 font-semibold text-xs hover:bg-tier-gold/10 transition flex items-center gap-1.5"
        >
          <Icon name="edit" className="!text-[16px]" /> Açılış Sayımını Gir
        </button>
      )}
      {dayStatus === "NONE" && (
        <button
          onClick={onOpen}
          className="bg-success text-white rounded-xl px-4 py-2 font-semibold text-xs hover:opacity-90 transition flex items-center gap-1.5"
        >
          <Icon name="lock_open" className="!text-[16px]" /> Günü Aç
        </button>
      )}
      {dayStatus === "OPEN" && (
        <button
          onClick={onClose}
          className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-xs hover:opacity-90 transition flex items-center gap-1.5"
        >
          <Icon name="lock" className="!text-[16px]" /> Günü Kapat (Kesinleştir)
        </button>
      )}
    </div>
  );
}

function OpenDayModal({ date, onClose, onSaved, showToast }) {
  const [opening, setOpening] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await financeService.openDay({
        date,
        openingBalance: opening === "" ? 0 : Number(opening),
        note: note.trim() || null,
      });
      showToast("Gün açıldı.", "success");
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.error ?? "İşlem başarısız.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-md w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
          <h3 className="font-bold text-lg text-on-surface flex items-center gap-2">
            <Icon name="lock_open" className="text-success !text-[22px]" /> Günü Aç
          </h3>
          <button onClick={onClose} className="p-1 rounded-full text-on-surface-variant hover:text-primary">
            <Icon name="close" className="!text-[22px]" />
          </button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4 text-sm overflow-y-auto flex-1">
          <p className="text-xs text-on-surface-variant">
            {new Date(date).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })} günü açılıyor.
          </p>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Açılış Devri (₺)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={opening}
              onChange={(e) => setOpening(e.target.value)}
              placeholder="0"
              className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
            />
            <p className="text-[11px] text-on-surface-variant mt-1">Kasada gün başında bulunan nakit. Boş bırakılırsa 0.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Açıklama</label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Opsiyonel"
              className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs">
              İptal
            </button>
            <button type="submit" disabled={saving} className="bg-success text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50">
              {saving ? "Açılıyor…" : "Günü Aç"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddTransactionModal({ date, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    type: "EXPENSE",
    category: "",
    amount: "",
    paymentMethod: "CASH",
    transactionDate: date,
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      showToast("Geçerli bir tutar girin.", "error");
      return;
    }
    setSaving(true);
    try {
      await financeService.createTransaction({
        type: form.type,
        category: form.category.trim() || null,
        amount: Number(form.amount),
        paymentMethod: form.paymentMethod,
        transactionDate: form.transactionDate,
        note: form.note.trim() || null,
      });
      showToast("İşlem eklendi.", "success");
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.error ?? "İşlem başarısız.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-md w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
          <h3 className="font-bold text-lg text-on-surface flex items-center gap-2">
            <Icon name="add_card" className="text-primary !text-[22px]" /> Gelir / Gider Ekle
          </h3>
          <button onClick={onClose} className="p-1 rounded-full text-on-surface-variant hover:text-primary">
            <Icon name="close" className="!text-[22px]" />
          </button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4 text-sm overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, type: "INCOME" })}
              className={`py-2 rounded-xl font-semibold text-sm border transition ${
                form.type === "INCOME"
                  ? "bg-success/15 text-success border-success/30"
                  : "border-outline-variant text-on-surface-variant hover:bg-surface-container-high"
              }`}
            >
              Gelir
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, type: "EXPENSE" })}
              className={`py-2 rounded-xl font-semibold text-sm border transition ${
                form.type === "EXPENSE"
                  ? "bg-error/15 text-error border-error/30"
                  : "border-outline-variant text-on-surface-variant hover:bg-surface-container-high"
              }`}
            >
              Gider
            </button>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Kategori</label>
            <input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Örn. Kira, Elektrik, Ürün Satışı"
              className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Tutar (₺)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Yöntem</label>
              <select
                value={form.paymentMethod}
                onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
                className="w-full border border-outline-variant rounded-xl px-3 py-2 bg-white outline-none focus:border-primary"
              >
                <option value="CASH">Nakit</option>
                <option value="CARD">Kart</option>
                <option value="TRANSFER">Havale</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Tarih</label>
            <input
              type="date"
              value={form.transactionDate}
              onChange={(e) => setForm({ ...form, transactionDate: e.target.value })}
              className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Açıklama</label>
            <input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Opsiyonel"
              className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs">
              İptal
            </button>
            <button type="submit" disabled={saving} className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50">
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- Alacaklar */

function ReceivablesTab({ showToast, initialQuery = "" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(initialQuery);
  const [range, setRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 90);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  });
  const [collectRow, setCollectRow] = useState(null);

  const load = () => {
    setLoading(true);
    financeService
      .receivables(range.from, range.to)
      .then((res) => setRows(res.data?.data ?? []))
      .catch(() => showToast("Alacaklar yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [range.from, range.to]); // eslint-disable-line react-hooks/exhaustive-deps

  // İstemci taraflı arama: müşteri adına göre süz (ileride binlerce kayıtta hızlı filtre)
  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr");
    if (!q) return rows;
    return rows.filter((r) => (r.customerName || "").toLocaleLowerCase("tr").includes(q));
  }, [rows, query]);

  const total = useMemo(() => filtered.reduce((s, r) => s + Number(r.remaining || 0), 0), [filtered]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="relative">
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Müşteri Ara</label>
            <Icon name="search" className="!text-[18px] absolute left-2.5 top-[34px] text-on-surface-variant pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Müşteri adı…"
              className="border border-outline-variant rounded-xl pl-9 pr-8 py-2 text-sm bg-white outline-none focus:border-primary w-56"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                title="Temizle"
                className="absolute right-2 top-[32px] p-0.5 rounded-full text-on-surface-variant hover:text-primary"
              >
                <Icon name="close" className="!text-[16px]" />
              </button>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Başlangıç</label>
            <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
              className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Bitiş</label>
            <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary" />
          </div>
        </div>
        <p className="text-xs text-on-surface-variant">
          {filtered.length} kayıt · Açık alacak toplamı: <strong className="text-error">{fmtTL(total)}</strong>
        </p>
      </div>

      <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-container-low text-on-surface-variant text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Müşteri</th>
                <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold">Durum</th>
                <th className="text-right px-4 py-3 font-semibold">Toplam</th>
                <th className="text-right px-4 py-3 font-semibold">Ödenen</th>
                <th className="text-right px-4 py-3 font-semibold">Kalan</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-on-surface-variant">Yükleniyor…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-on-surface-variant">{query ? "Aramayla eşleşen alacak yok." : "Açık alacak yok."}</td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.appointmentId} className="hover:bg-surface-container-low/50">
                    <td className="px-4 py-3 font-semibold text-on-surface">{r.customerName}</td>
                    <td className="px-4 py-3">{r.appointmentDate ? new Date(r.appointmentDate).toLocaleDateString("tr-TR") : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${(RECEIVABLE_STATUS[r.status] || RECEIVABLE_STATUS.SCHEDULED).cls}`}>
                        {(RECEIVABLE_STATUS[r.status] || RECEIVABLE_STATUS.SCHEDULED).label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">{fmtTL(r.total)}</td>
                    <td className="px-4 py-3 text-right text-success">{fmtTL(r.paid)}</td>
                    <td className="px-4 py-3 text-right font-bold text-error">{fmtTL(r.remaining)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setCollectRow(r)}
                        className="text-xs font-semibold text-primary hover:bg-primary/10 rounded-lg px-3 py-1.5 transition flex items-center gap-1 ml-auto"
                      >
                        <Icon name="payments" className="!text-[16px]" /> Tahsil Et
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {collectRow && (
        <PaymentCollectModal
          row={collectRow}
          onClose={() => setCollectRow(null)}
          onSaved={() => {
            setCollectRow(null);
            load();
          }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

